import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, ShoppingCart, FileCheck, FileClock, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { formatBRL, formatDate, statusInfo } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';

export default function Sales() {
  const { user } = useAuth();
  const isManager = ['admin', 'supervisor'].includes(user?.role);
  const [sales, setSales] = useState([]);
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [form, setForm] = useState({ trip_id: '', client_id: '', vendedor_id: '', valor_total: 0, valor_pago: 0, comissao_percentual: 0, status: 'aberta', obs: '', pagamentos: [] });

  const load = () => {
    Promise.all([api.get('/sales'), api.get('/trips'), api.get('/clients'), api.get('/users/vendedores'), api.get('/suppliers')])
      .then(([s, t, c, v, sup]) => { setSales(s.data); setTrips(t.data); setClients(c.data); setVendedores(v.data); setSuppliers(sup.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ trip_id: '', client_id: '', vendedor_id: user?.role === 'vendedor' ? user.user_id : '', valor_total: 0, valor_pago: 0, comissao_percentual: 0, status: 'aberta', obs: '', pagamentos: [] });
    setOpen(true);
  };
  const openEdit = (s) => {
    // Preserva valor já pago de vendas antigas (sem lista de pagamentos) como um lançamento
    const pagamentos = (s.pagamentos && s.pagamentos.length)
      ? s.pagamentos
      : (s.valor_pago > 0 ? [{ valor: s.valor_pago, data: '' }] : []);
    setEditing(s);
    setForm({ ...s, pagamentos });
    setOpen(true);
  };

  const totalPago = (form.pagamentos || []).reduce((sum, p) => sum + Number(p.valor || 0), 0);
  const addPag = () => setForm((f) => ({ ...f, pagamentos: [...(f.pagamentos || []), { valor: '', data: new Date().toISOString().slice(0, 10), fornecedor_id: '' }] }));
  const updatePag = (i, key, val) => setForm((f) => { const arr = [...(f.pagamentos || [])]; arr[i] = { ...arr[i], [key]: val }; return { ...f, pagamentos: arr }; });
  const removePag = (i) => setForm((f) => ({ ...f, pagamentos: (f.pagamentos || []).filter((_, idx) => idx !== i) }));

  const onTrip = (v) => {
    const t = trips.find((x) => x.trip_id === v);
    setForm((f) => ({ ...f, trip_id: v, valor_total: t ? t.preco_venda : f.valor_total }));
  };
  const onVendedor = (v) => {
    const ve = vendedores.find((x) => x.user_id === v);
    setForm((f) => ({ ...f, vendedor_id: v, comissao_percentual: ve ? (ve.comissao_percentual || 0) : f.comissao_percentual }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.trip_id || !form.client_id) { toast.error('Selecione viagem e cliente'); return; }
    setSaving(true);
    const payload = {
      trip_id: form.trip_id, client_id: form.client_id,
      vendedor_id: form.vendedor_id || null,
      valor_total: Number(form.valor_total || 0),
      comissao_percentual: Number(form.comissao_percentual || 0), status: form.status, obs: form.obs || '',
    };
    if (isManager) {
      const pags = (form.pagamentos || [])
        .filter((p) => p.valor !== '' && p.valor != null && Number(p.valor) !== 0)
        .map((p) => ({ valor: Number(p.valor || 0), data: p.data || '', fornecedor_id: p.fornecedor_id || '' }));
      if (pags.some((p) => !p.fornecedor_id)) {
        toast.error('Selecione o fornecedor de cada pagamento');
        setSaving(false);
        return;
      }
      payload.pagamentos = pags;
      payload.valor_pago = pags.reduce((s, p) => s + p.valor, 0);
    } else {
      payload.valor_pago = 0;
    }
    try {
      if (editing) { await api.put(`/sales/${editing.sale_id}`, payload); toast.success('Venda atualizada'); }
      else { await api.post('/sales', payload); toast.success('Venda registrada'); }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/sales/${delTarget.sale_id}`); toast.success('Venda removida'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao remover'); }
    finally { setDelTarget(null); }
  };

  const downloadVoucher = async (saleId, name) => {
    try {
      const r = await api.get(`/vouchers/${saleId}`);
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${r.data.pdf_base64}`;
      link.download = `voucher-${name || saleId}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (err) { toast.error('Não foi possível baixar o voucher'); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Vendas</h2>
          <p className="text-sm text-muted-foreground">Registre vendas, comissões e acompanhe contratos.</p>
        </div>
        <Button onClick={openNew} data-testid="sales-new-button"><Plus className="h-4 w-4 mr-2" /> Nova venda</Button>
      </div>

      <Card className="p-4">
        {loading ? <p className="text-muted-foreground py-8 text-center">Carregando...</p> : sales.length === 0 ? (
          <div className="py-12 text-center"><ShoppingCart className="h-8 w-8 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Nenhuma venda registrada.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="sales-table">
              <TableHeader><TableRow>
                <TableHead>Cliente</TableHead><TableHead>Viagem</TableHead><TableHead>Vendedor</TableHead>
                <TableHead className="text-right">Valor</TableHead><TableHead className="text-right">Pago</TableHead>
                <TableHead className="text-right">Comissão</TableHead><TableHead>Status</TableHead><TableHead>Contrato</TableHead><TableHead className="w-10"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sales.map((s) => {
                  const si = statusInfo(s.status);
                  return (
                    <TableRow key={s.sale_id}>
                      <TableCell className="font-medium">{s.cliente_nome}</TableCell>
                      <TableCell className="text-muted-foreground">{s.trip_nome}</TableCell>
                      <TableCell className="text-muted-foreground">{s.vendedor_nome || '-'}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatBRL(s.valor_total)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatBRL(s.valor_pago)}
                        {s.pagamentos && s.pagamentos.length > 0 && (
                          <div className="text-[11px] text-muted-foreground font-normal" data-testid={`sale-pag-dates-${s.sale_id}`}>
                            {s.pagamentos.filter((p) => p.data).length === 0
                              ? `${s.pagamentos.length} pagamento(s)`
                              : s.pagamentos.filter((p) => p.data).map((p) => formatDate(p.data)).join(', ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatBRL(s.comissao_valor)}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${si.cls}`}>{si.label}</span></TableCell>
                      <TableCell>{s.contrato_assinado ? <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)] gap-1"><FileCheck className="h-3 w-3" /> Assinado</Badge> : <Badge variant="secondary" className="gap-1"><FileClock className="h-3 w-3" /> Pendente</Badge>}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`sales-row-actions-${s.sale_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => downloadVoucher(s.sale_id, s.trip_nome)} data-testid={`sales-voucher-${s.sale_id}`}><Ticket className="h-4 w-4 mr-2" /> Baixar voucher</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEdit(s)}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
                            {isManager && <DropdownMenuItem onClick={() => setDelTarget(s)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar venda' : 'Nova venda'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {!editing && (
              <>
                <div><Label>Viagem *</Label>
                  <Select value={form.trip_id} onValueChange={onTrip}>
                    <SelectTrigger className="mt-1.5" data-testid="sale-trip"><SelectValue placeholder="Selecione a viagem" /></SelectTrigger>
                    <SelectContent>{trips.map((t) => <SelectItem key={t.trip_id} value={t.trip_id}>{t.nome} — {t.destino}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Cliente *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="sale-client"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}
            {isManager && (
              <div><Label>Vendedor</Label>
                <Select value={form.vendedor_id} onValueChange={onVendedor}>
                  <SelectTrigger className="mt-1.5" data-testid="sale-vendedor"><SelectValue placeholder="Selecione o vendedor" /></SelectTrigger>
                  <SelectContent>{vendedores.map((v) => <SelectItem key={v.user_id} value={v.user_id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Valor total (R$)</Label><Input type="number" step="0.01" value={form.valor_total} onChange={(e) => setForm({ ...form, valor_total: e.target.value })} className="mt-1.5" data-testid="sale-valor" /></div>
              <div><Label>Comissão (%)</Label><Input type="number" step="0.01" value={form.comissao_percentual} onChange={(e) => setForm({ ...form, comissao_percentual: e.target.value })} className="mt-1.5" /></div>
              {isManager && (
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="aberta">Aberta</SelectItem><SelectItem value="paga">Paga</SelectItem><SelectItem value="cancelada">Cancelada</SelectItem></SelectContent>
                </Select>
              </div>
              )}
            </div>
            {isManager && (
              <div className="rounded-lg border p-4 space-y-3" data-testid="sale-payments">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Pagamentos</Label>
                  <span className="text-sm text-muted-foreground">Total pago: <span className="font-semibold text-foreground">{formatBRL(totalPago)}</span></span>
                </div>
                {(form.pagamentos || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhum pagamento registrado. Adicione o valor e a data de cada pagamento.</p>
                )}
                {(form.pagamentos || []).map((p, i) => (
                  <div key={i} className="flex flex-wrap gap-2 items-end border-b border-dashed border-border/60 pb-3 last:border-0 last:pb-0">
                    <div className="w-28"><Label className="text-xs text-muted-foreground">Valor (R$)</Label><Input type="number" step="0.01" value={p.valor} onChange={(e) => updatePag(i, 'valor', e.target.value)} className="mt-1" data-testid={`pag-valor-${i}`} /></div>
                    <div className="w-40"><Label className="text-xs text-muted-foreground">Data do pagamento</Label><Input type="date" value={p.data || ''} onChange={(e) => updatePag(i, 'data', e.target.value)} className="mt-1" data-testid={`pag-data-${i}`} /></div>
                    <div className="flex-1 min-w-[160px]">
                      <Label className="text-xs text-muted-foreground">Fornecedor (destino) *</Label>
                      <Select value={p.fornecedor_id || ''} onValueChange={(v) => updatePag(i, 'fornecedor_id', v)}>
                        <SelectTrigger className="mt-1" data-testid={`pag-fornecedor-${i}`}><SelectValue placeholder="Selecione o fornecedor" /></SelectTrigger>
                        <SelectContent>
                          {suppliers.map((sup) => <SelectItem key={sup.supplier_id} value={sup.supplier_id}>{sup.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePag(i)} data-testid={`pag-remove-${i}`} aria-label="Remover pagamento"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
                <Button type="button" variant="secondary" size="sm" onClick={addPag} data-testid="pag-add"><Plus className="h-4 w-4 mr-1" /> Adicionar pagamento</Button>
              </div>
            )}
            <div><Label>Observações</Label><Textarea value={form.obs} onChange={(e) => setForm({ ...form, obs: e.target.value })} rows={2} className="mt-1.5" /></div>
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving} data-testid="sale-save">{saving ? 'Salvando...' : 'Salvar venda'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir venda?</AlertDialogTitle><AlertDialogDescription>Esta ação removerá a venda e o contrato vinculado.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
